import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import HomePage from "@/pages/HomePage";
import { AREA_SLUGS, areaPath } from "@/data/areaContent";
const BookingPage = lazy(() => import("@/pages/BookingPage"));
const BookingSuccessPage = lazy(() => import("@/pages/BookingSuccessPage"));
const BookingCancelPage = lazy(() => import("@/pages/BookingCancelPage"));
const ManageBookingPage = lazy(() => import("@/pages/ManageBookingPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const PrintableLeaflet = lazy(() => import("@/pages/PrintableLeaflet"));
const PricingRulesPage = lazy(() => import("@/pages/PricingRulesPage"));
const ServicesManagementPage = lazy(() => import("@/pages/ServicesManagementPage"));
const CustomersPage = lazy(() => import("@/pages/CustomersPage"));
const ReportsPage = lazy(() => import("@/pages/ReportsPage"));
const EnquiriesPage = lazy(() => import("@/pages/EnquiriesPage"));
const CalendarPage = lazy(() => import("@/pages/CalendarPage"));
const PackagePage = lazy(() => import("@/pages/PackagePage"));
const AccountPage = lazy(() => import("@/pages/AccountPage"));
const MyBookingsPage = lazy(() => import("@/pages/MyBookingsPage"));
import AuthCallback from "@/pages/AuthCallback";
const AdminReviewsPage = lazy(() => import("@/pages/AdminReviewsPage"));
const AreaPage = lazy(() => import("@/pages/AreaPage"));
const DriversPage = lazy(() => import("@/pages/DriversPage"));
const PromoCodesPage = lazy(() => import("@/pages/PromoCodesPage"));
const DriverLogin = lazy(() => import("@/pages/DriverLogin"));
const DriverDiaryPage = lazy(() => import("@/pages/DriverDiaryPage"));
import PromoBar from "@/components/PromoBar";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Intercept the Emergent Auth callback (#session_id=...) SYNCHRONOUSLY during
// render before the normal <Routes> tree runs (per the auth playbook — a
// useEffect check would race with other pages' auth checks).
// Auth tokens are httpOnly cookies — always send them with API calls
axios.defaults.withCredentials = true;


function AppRouter() {
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Hide the promo bar on admin + booking success/cancel pages
  const path = location.pathname || "/";
  const hidePromo = path.startsWith("/admin") || path.startsWith("/driver") || path.startsWith("/booking/") || path === "/leaflet";
  return (
    <>
      {!hidePromo && <PromoBar />}
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[#0A192F]"><div className="loading-spinner" /></div>}>
      <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/booking" element={<BookingPage />} />
      <Route path="/booking/success" element={<BookingSuccessPage />} />
      <Route path="/booking/cancel" element={<BookingCancelPage />} />
      <Route path="/manage-booking/:token" element={<ManageBookingPage />} />
      <Route path="/account" element={<AccountPage />} />
      <Route path="/my-bookings" element={<MyBookingsPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/calendar" element={<CalendarPage />} />
      <Route path="/admin/pricing" element={<PricingRulesPage />} />
      <Route path="/admin/services" element={<ServicesManagementPage />} />
      <Route path="/admin/customers" element={<CustomersPage />} />
      <Route path="/admin/reports" element={<ReportsPage />} />
      <Route path="/admin/enquiries" element={<EnquiriesPage />} />
      <Route path="/admin/reviews" element={<AdminReviewsPage />} />
      <Route path="/admin/drivers" element={<DriversPage />} />
      <Route path="/admin/promo-codes" element={<PromoCodesPage />} />
      <Route path="/driver/login" element={<DriverLogin />} />
      <Route path="/driver" element={<DriverDiaryPage />} />
      <Route path="/leaflet" element={<PrintableLeaflet />} />
      <Route path="/mini-valet" element={<PackagePage />} />
      <Route path="/full-valet" element={<PackagePage />} />
      <Route path="/showroom-valet" element={<PackagePage />} />
      <Route path="/deep-clean-interior" element={<PackagePage />} />
      <Route path="/deep-clean-exterior" element={<PackagePage />} />
      <Route path="/soft-top-restoration" element={<PackagePage />} />
      {AREA_SLUGS.map((slug) => <Route key={slug} path={areaPath(slug)} element={<AreaPage slug={slug} />} />)}
    </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <div className="App min-h-screen bg-[#0A192F]">
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;
